import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "wouter";
import { format } from "date-fns";
import { ko } from "date-fns/locale";
import { MapPin, Calendar, TrendingUp } from "lucide-react";
import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { Article } from "@shared/schema";
import { PageContainer } from "@/components/layout/PageContainer";

const REGIONS = [
  { id: "ALL", label: "전체", color: "bg-blue-500" },
  { id: "NSW", label: "NSW", color: "bg-red-500" },
  { id: "VIC", label: "VIC", color: "bg-purple-500" },
  { id: "QLD", label: "QLD", color: "bg-yellow-500" },
  { id: "SA", label: "SA", color: "bg-green-500" },
  { id: "WA", label: "WA", color: "bg-orange-500" },
  { id: "TAS", label: "TAS", color: "bg-pink-500" },
  { id: "NT", label: "NT", color: "bg-teal-500" },
  { id: "ACT", label: "ACT", color: "bg-indigo-500" },
];

export default function RegionalNews() {
  const [selectedRegion, setSelectedRegion] = useState("ALL");

  const { data: articles, isLoading } = useQuery<Article[]>({
    queryKey: ['/api/articles', { status: 'published', region: selectedRegion !== "ALL" ? selectedRegion : undefined }],
  });

  const filteredArticles = articles?.filter(article =>
    article.category === 'community' || article.region !== 'ALL'
  ) || [];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-background border-b">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="flex items-center gap-3 mb-4">
            <MapPin className="h-8 w-8 text-primary" />
            <h1 className="text-4xl font-bold">주별 소식</h1>
          </div>
          <p className="text-lg text-muted-foreground max-w-2xl">
            호주 각 주(state)별 지역 뉴스와 교민 소식을 한눈에 확인하세요
          </p>
        </div>
      </div>

      <PageContainer size="wide">
        {/* Region Tabs */}
        <Tabs value={selectedRegion} onValueChange={setSelectedRegion} className="mb-8">
          <TabsList className="grid grid-cols-3 md:grid-cols-9 w-full mb-8">
            {REGIONS.map((region) => (
              <TabsTrigger
                key={region.id}
                value={region.id}
                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                data-testid={`tab-region-${region.id}`}
              >
                {region.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {REGIONS.map((region) => (
            <TabsContent key={region.id} value={region.id} className="mt-0">
              {isLoading ? (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {[...Array(6)].map((_, i) => (
                    <Card key={i}>
                      <CardHeader>
                        <Skeleton className="h-6 w-3/4 mb-2" />
                        <Skeleton className="h-4 w-1/2" />
                      </CardHeader>
                      <CardContent>
                        <Skeleton className="h-20 w-full" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : filteredArticles.length === 0 ? (
                <Card>
                  <CardContent className="py-16 text-center">
                    <MapPin className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-xl font-semibold mb-2">지역 뉴스가 없습니다</h3>
                    <p className="text-muted-foreground">
                      {selectedRegion === "ALL" ? "아직 등록된 지역 뉴스가 없습니다" : `${region.label} 지역의 뉴스가 아직 없습니다`}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-6">
                  {/* Statistics */}
                  <div className="grid gap-4 md:grid-cols-3">
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium text-muted-foreground">전체 기사</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{filteredArticles.length}개</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium text-muted-foreground">오늘의 뉴스</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {filteredArticles.filter(a => {
                            const today = new Date();
                            const articleDate = new Date(a.publishedAt || a.createdAt);
                            return articleDate.toDateString() === today.toDateString();
                          }).length}개
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium text-muted-foreground">이번 주</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {filteredArticles.filter(a => {
                            const weekAgo = new Date();
                            weekAgo.setDate(weekAgo.getDate() - 7);
                            const articleDate = new Date(a.publishedAt || a.createdAt);
                            return articleDate >= weekAgo;
                          }).length}개
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Article Grid */}
                  <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {filteredArticles.map((article) => (
                      <Link key={article.id} href={`/article/${article.id}`}>
                        <Card className="h-full hover-elevate active-elevate-2 cursor-pointer transition-all" data-testid={`card-article-${article.id}`}>
                          {article.imageUrl && (
                            <div className="aspect-video overflow-hidden rounded-t-lg">
                              <img
                                src={article.imageUrl}
                                alt={article.titleKo || article.title}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}
                          <CardHeader>
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              {article.region && article.region !== 'ALL' && (
                                <Badge variant="secondary" className="text-xs">
                                  <MapPin className="h-3 w-3 mr-1" />
                                  {REGIONS.find(r => r.id === article.region)?.label || article.region}
                                </Badge>
                              )}
                              {article.isBreaking && (
                                <Badge variant="destructive" className="text-xs">
                                  <TrendingUp className="h-3 w-3 mr-1" />
                                  속보
                                </Badge>
                              )}
                            </div>
                            <CardTitle className="line-clamp-2 text-lg">
                              {article.titleKo || article.title}
                            </CardTitle>
                            <CardDescription className="flex items-center gap-2 text-xs">
                              <Calendar className="h-3 w-3" />
                              {format(new Date(article.publishedAt || article.createdAt), "yyyy년 MM월 dd일", { locale: ko })}
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            <p className="text-sm text-muted-foreground line-clamp-3">
                              {article.summaryKo || article.summary || ""}
                            </p>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </PageContainer>
    </div>
  );
}
